import type { ApplicationError, ApplicationResult, AppointmentProjection, AppointmentStatus, SalonSettings } from './contracts.js';

const fail = <T>(error: ApplicationError): ApplicationResult<T> => ({ ok: false, error });
export const minutesOfDay = (iso: string): number => { const date = new Date(iso); return date.getHours() * 60 + date.getMinutes(); };
export const minutesFromClock = (clock: string): number => { const [hour = 0, minute = 0] = clock.split(':').map(Number); return hour * 60 + minute; };
export const durationMinutes = (startAt: string, endAt: string): number => Math.round((new Date(endAt).getTime() - new Date(startAt).getTime()) / 60000);
export const addMinutes = (iso: string, minutes: number): string => new Date(new Date(iso).getTime() + minutes * 60000).toISOString();
export const isBlockingStatus = (status: AppointmentStatus): boolean => !['cancelled', 'no_show'].includes(status);

export function validateAppointment(
  candidate: AppointmentProjection,
  appointments: AppointmentProjection[],
  settings: SalonSettings,
  excludedId?: string
): ApplicationResult<AppointmentProjection> {
  const start = new Date(candidate.startAt); const end = new Date(candidate.endAt);
  if (!Number.isFinite(start.getTime()) || !Number.isFinite(end.getTime()) || end <= start) {
    return fail({ code: 'APPOINTMENT_INVALID_RANGE', message: 'Bitiş zamanı başlangıçtan sonra olmalıdır.' });
  }
  const duration = durationMinutes(candidate.startAt, candidate.endAt);
  if (duration < settings.slotIntervalMinutes) return fail({ code: 'APPOINTMENT_INVALID_RANGE', message: `En kısa randevu ${settings.slotIntervalMinutes} dakikadır.` });
  if (minutesOfDay(candidate.startAt) % settings.slotIntervalMinutes !== 0 || duration % settings.slotIntervalMinutes !== 0) {
    return fail({ code: 'APPOINTMENT_SLOT_MISMATCH', message: `Saat ve süre ${settings.slotIntervalMinutes} dakikalık slotlara oturmalıdır.` });
  }
  if (start.toDateString() !== end.toDateString() || minutesOfDay(candidate.startAt) < minutesFromClock(settings.openingTime) || minutesOfDay(candidate.endAt) > minutesFromClock(settings.closingTime)) {
    return fail({ code: 'APPOINTMENT_OUTSIDE_WORKING_HOURS', message: `Randevu ${settings.openingTime}–${settings.closingTime} çalışma saatleri içinde olmalıdır.` });
  }
  if (isBlockingStatus(candidate.status)) {
    const occupiedStart = start.getTime() - candidate.beforeBufferMinutes * 60000;
    const occupiedEnd = end.getTime() + candidate.afterBufferMinutes * 60000;
    const conflict = appointments.find((item) => item.id !== excludedId && item.staffId === candidate.staffId && isBlockingStatus(item.status)
      && occupiedStart < new Date(item.endAt).getTime() + item.afterBufferMinutes * 60000
      && occupiedEnd > new Date(item.startAt).getTime() - item.beforeBufferMinutes * 60000);
    if (conflict) return fail({ code: 'APPOINTMENT_TIME_CONFLICT', message: `${candidate.staffName} için bu zaman aralığı dolu.`, details: { conflictingAppointmentId: conflict.id } });
  }
  return { ok: true, value: candidate };
}

const transitions: Record<AppointmentStatus, AppointmentStatus[]> = {
  scheduled: ['confirmed', 'arrived', 'cancelled', 'no_show'], confirmed: ['arrived', 'in_service', 'cancelled', 'no_show'],
  arrived: ['in_service', 'cancelled', 'no_show'], in_service: ['completed', 'cancelled'], completed: [], cancelled: ['scheduled'], no_show: ['scheduled']
};
export function validateStatusTransition(from: AppointmentStatus, to: AppointmentStatus): ApplicationResult<AppointmentStatus> {
  if (from === to || transitions[from].includes(to)) return { ok: true, value: to };
  return fail({ code: 'APPOINTMENT_STATUS_TRANSITION_INVALID', message: `${from} durumundan ${to} durumuna geçilemez.` });
}
